import { Grid, Button } from "@mui/material";
import { useDispatch } from "react-redux";
import { Table, Input, ConfigProvider, Select, Button as BtnAntD } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import EditIcon from "@mui/icons-material/Edit";
import CreateUpdateCityMunicipalities from "./createUpdateCityMunicipalities";
import useCheckPermission from "hooks/useCheckPermission";
import useDebounce from "hooks/useDebounce";
import { getAllCityMunicipalities } from "store";
import { EmptyData } from "components/mnl/global/emptyData";
import { getProvinceLookUp } from "store";
import { useCurrentHeight } from "utils/getScreenSize";
import { useCallback, useEffect, useState } from "react";

const GetAllCityMunicipalities = () => {
  const dispatch = useDispatch();
  const { handleVerifyPermission } = useCheckPermission();
  const accessRightsToModify = handleVerifyPermission(
    "Administration:CityMunicipality:Modify"
  );
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const debouncedInputValue = useDebounce(searchText);
  const [sortStr, setSortStr] = useState("name");
  const [sortDirection, setSortDirection] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [cityMunicipalitiesData, setCityMunicipalitiesData] = useState([]);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);

  const [isForUpdate, setIsForUpdate] = useState(false);
  const [idForUpdate, setIdForUpdate] = useState(0);

  const [provinceData, setProvinceData] = useState([]);

  const LoadData = useCallback(() => {
    //HANDLE LOADING
    if (
      getAllCityMunicipalities.pending().type ===
      "citymunicipalities/get-all-citymunicipalities/pending"
    ) {
      setIsLoading(true);
    }

    dispatch(
      getAllCityMunicipalities({
        search: debouncedInputValue,
        pageSize: pageSize,
        pageNumber: current,
        sortField: sortStr,
        sortDirection: sortDirection,
      })
    ).then((element) => {
      if (
        element.type ===
        "citymunicipalities/get-all-citymunicipalities/fulfilled"
      ) {
        setTotal(element.payload.data.totalPages * pageSize);

        //GET PROVINCE
        dispatch(getProvinceLookUp()).then((elementProvince) => {
          if (
            elementProvince.type === "provinces/get-provinces-look-up/fulfilled"
          ) {
            setIsLoading(false);
            setProvinceData(elementProvince.payload.data);
            const newCityMunicipalities = element.payload.data.items.map(
              (newElement) => {
                return {
                  ...newElement,
                  provinceName: elementProvince.payload.data.filter(
                    (elementCountryTemp) =>
                      elementCountryTemp.id === newElement.provinceId
                  )[0].name,
                };
              }
            );

            setCityMunicipalitiesData(newCityMunicipalities);
          }
        });
      }
    });
  }, [
    dispatch,
    debouncedInputValue,
    pageSize,
    sortStr,
    current,
    sortDirection,
  ]);

  useEffect(() => {
    LoadData();
  }, [dispatch, LoadData]);

  const onChange = (page) => {
    setCurrent(page);
  };
  const onShowSizeChange = (current, pageSize) => {
    setPageSize(pageSize);
  };
  const handleChangeSearch = (val) => {
    setIsLoading(true);
    setSearchText(val.target.value);
    setCurrent(1);
  };

  const renderEmptyData = () => (
    <EmptyData
      description="No City Municipality Detected"
      action={showCreateUpdate}
      btnTitle="Add New City Municipality"
      renderButton={accessRightsToModify}
    />
  );

  //MODAL CREATE UPDATE
  const [openCreateUpdate, setOpenCreateUpdate] = useState(false);

  const handleCancel = () => {
    setOpenCreateUpdate(false);
    setIsForUpdate(false);
    setIdForUpdate(0);
  };

  const showCreateUpdate = () => {
    setOpenCreateUpdate(true);
  };

  const handleSubmit = () => {
    LoadData();
    setOpenCreateUpdate(false);
    setIdForUpdate(0);
    setIsForUpdate(false);
  };

  const handleUpdate = (id) => {
    setIsForUpdate(true);
    setIdForUpdate(id);
  };

  const handleChangeSortField = (value) => {
    setSortStr(value);
  };

  const handleChangeSortDirection = (value) => {
    setSortDirection(value);
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 480,
    },
    {
      title: "Province",
      dataIndex: "provinceName",
      key: "provinceName",
      width: 100,
    },
    {
      title: "",
      dataIndex: "",
      key: "",
      width: 150,
      render: (rec, row) => (
        <>
          {accessRightsToModify ? (
            <BtnAntD
              size="small"
              type="text"
              style={{
                marginLeft: 2,
              }}
              icon={<EditIcon fontSize="inherit" />}
              className="bg-orange-600 text-white hover:enabled:bg-orange-400 hover:enabled:text-white"
              onClick={() => {
                handleUpdate(row);
                showCreateUpdate();
              }}
            >
              Update
            </BtnAntD>
          ) : null}
        </>
      ),
    },
  ];

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3} lg={3}>
          <Input
            size=""
            placeholder="Search City Municipality Provider"
            prefix={<SearchOutlined />}
            onChange={handleChangeSearch}
            value={searchText}
          />
        </Grid>
        <Grid item xs={12} md={3} lg={3}>
          <Select
            defaultValue={sortStr}
            onChange={handleChangeSortField}
            style={{
              width: 120,
            }}
            options={[
              {
                value: "name",
                label: "Name",
              },
              {
                value: "provinceId",
                label: "Province",
              },
            ]}
          />
          <Select
            defaultValue={sortDirection}
            onChange={handleChangeSortDirection}
            style={{
              width: 120,
              marginLeft: 2,
            }}
            options={[
              {
                value: 0,
                label: "Ascending",
              },
              {
                value: 1,
                label: "Descending ",
              },
            ]}
          />
        </Grid>

        {cityMunicipalitiesData.length !== 0 && accessRightsToModify ? (
          <Grid item xs={12} md={4} lg={4}>
            <Button
              startIcon={<AddOutlinedIcon />}
              variant="text"
              onClick={showCreateUpdate}
            >
              New City Municipality
            </Button>
          </Grid>
        ) : null}
      </Grid>
      <ConfigProvider renderEmpty={renderEmptyData}>
        <Table
          rowClassName="odd:bg-slate-300"
          columns={
            accessRightsToModify
              ? columns
              : columns.filter((col) => col.dataIndex !== "")
          }
          dataSource={cityMunicipalitiesData}
          loading={isLoading}
          size="small"
          pagination={{
            total: total,
            showSizeChanger: true,
            onShowSizeChange: onShowSizeChange,
            current: current,
            onChange: onChange,
            showQuickJumper: true,
            position: ["bottomRight"],
          }}
          bordered={true}
          scroll={{ y: useCurrentHeight() - 330 }}
          style={{ marginTop: 10 }}
          rowKey={(record) => record.id}
        />
      </ConfigProvider>
      <CreateUpdateCityMunicipalities
        handleCancel={handleCancel}
        openCreateUpdate={openCreateUpdate}
        handleSubmit={handleSubmit}
        isForUpdate={isForUpdate}
        idForUpdate={idForUpdate}
        provinceData={provinceData}
      />
    </>
  );
};
export default GetAllCityMunicipalities;
